import { layoutModeTokens, useLayoutMode } from '@/ui/layouts/layoutMode';
import { Box, Text } from '@/ui/primitives';
import { s } from '../../theme/tokens';
import type { ReactElement, ReactNode } from 'react';

type PageHeaderProps = {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  align?: 'center' | 'start';
};

export const PageHeader = ({
  title,
  subtitle,
  actions,
  align = 'center',
}: PageHeaderProps): ReactElement => {
  const layoutMode = useLayoutMode();
  const isCompact = layoutMode === layoutModeTokens.compact;
  const resolvedAlignItems = isCompact ? 'flex-start' : align === 'start' ? 'flex-start' : 'center';
  const useFixedHeaderHeight = subtitle === undefined;

  const titleNode =
    typeof title === 'string' || typeof title === 'number' ? (
      <Text role="sectionTitle" component="h1">
        {title}
      </Text>
    ) : (
      title
    );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: resolvedAlignItems,
        gap: s('md'),
        ...(useFixedHeaderHeight ? { minHeight: '32px' } : {}),
      }}
    >
      <Box
        sx={{
          minWidth: 0,
          flex: '1 1 auto',
          ...(useFixedHeaderHeight
            ? {
                minHeight: '32px',
                display: 'flex',
                alignItems: 'center',
              }
            : {}),
          '&, & *': {
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
          },
        }}
      >
        {titleNode}
        {subtitle ? (
          <Text role="bodyMuted" sx={{ marginTop: s('xs') }}>
            {subtitle}
          </Text>
        ) : null}
      </Box>
      {actions ? (
        <Box
          sx={{
            flexShrink: 0,
            ...(useFixedHeaderHeight
              ? {
                  minHeight: '32px',
                  display: 'flex',
                  alignItems: 'center',
                }
              : {}),
          }}
        >
          {actions}
        </Box>
      ) : null}
    </Box>
  );
};
